import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchData } from '../../../helpers/apiUtils';
import { SNIPPET_GLOBAL_SITE_ID, SnippetListRow, SnippetOwnership } from './types';

interface Props {
  /** Der NAME, um den es geht — die Aktion gilt dem Namen, nicht einer Zeile. */
  slug: string | null;
  /** Alle Fassungen dieses Namens, damit die Vorschau sagen kann, WELCHE wo landet. */
  fassungen: SnippetListRow[];
  onAbbrechen: () => void;
  /** `zielPortale` ist leer, wenn ALLE gemeint sind — dann schickt der Aufrufer keinen Filter. */
  onAusspielen: (zielPortale: number[]) => void;
}

/**
 * Zeigt VOR dem Schreiben, welches Portal welche Fassung bekommt — und lässt einzelne abwählen.
 *
 * ============================================================================================
 * WARUM ES DIESEN DIALOG GIBT
 * ============================================================================================
 * Bei der Abnahme am 2026-08-25 stand der Ausspielen-Knopf in der Zeile EINER Fassung, die Aktion
 * galt aber dem ganzen Namen. Ein Klick auf die Trading-Treff-Zeile schrieb auf alle sechs
 * Portale (A40-B1). Der Knopf sitzt jetzt in der Namenszeile — aber „sichtbarer" allein reicht
 * nicht: man muss auch „nur dieses eine" ausdrücken können. Dafür die Auswahl hier plus
 * `zielPortale` am Endpunkt.
 *
 * ⚠ DIESE ANZEIGE IST EINE VORSCHAU, KEINE ZUSAGE. Zwischen dem Öffnen des Dialogs und dem POST
 * kann sich die Auflösung ändern (jemand legt eine Portal-Fassung an, schaltet eine ab). Der
 * Server löst beim Ausführen NEU auf und ist massgeblich; was tatsächlich passiert ist, steht in
 * seiner Antwort — und die zeigt der Aufrufer danach an, nicht diese Tabelle.
 */
const SnippetAusspielenDialog: React.FC<Props> = ({
  slug,
  fassungen,
  onAbbrechen,
  onAusspielen,
}) => {
  const [besitz, setBesitz] = useState<SnippetOwnership[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [abgewaehlt, setAbgewaehlt] = useState<Set<number>>(new Set());

  const laden = useCallback(async () => {
    if (!slug) return;
    setBesitz(null);
    setFehler(null);
    setAbgewaehlt(new Set());
    const r = await fetchData<SnippetOwnership[]>(
      `/api/broker-hub/snippets/ownership/${encodeURIComponent(slug)}`,
    );
    if (r.success && Array.isArray(r.data)) setBesitz(r.data);
    else setFehler(r.error || 'Die Zuordnung liess sich nicht laden.');
  }, [slug]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const beschreibung = (o: SnippetOwnership) => {
    if (!o.owner_id) {
      return { text: 'wird geräumt — hier gilt danach nichts', ton: 'warning' as const };
    }
    const f = fassungen.find((x) => Number(x.id) === Number(o.owner_id));
    const global = Number(f?.site_id) === SNIPPET_GLOBAL_SITE_ID;
    return {
      text: global ? 'bekommt die globale Fassung' : `bekommt die eigene Fassung (${o.site_key})`,
      ton: 'default' as const,
    };
  };

  const gewaehlt = (besitz ?? []).filter((o) => !abgewaehlt.has(Number(o.site_id)));
  const alleGewaehlt = besitz !== null && abgewaehlt.size === 0;

  return (
    <Dialog open={slug !== null} onClose={onAbbrechen} maxWidth="sm" fullWidth>
      <DialogTitle>
        Ausspielen: <code>{slug}</code>
      </DialogTitle>
      <DialogContent>
        {/*
          ⚠ Der Hinweis steht bewusst OBEN und nennt die Zahl. „Auf alle Zielportale ausspielen"
          als Tooltip war formal richtig und wurde trotzdem überlesen — weil der Knopf in einer
          Zeile stand, die ein einzelnes Portal benannte.
        */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Diese Aktion gilt dem <strong>Namen</strong>, nicht einer einzelnen Fassung. Der Hub
          stellt auf jedem gewählten Portal den Stand her, der dort gilt.
        </Alert>

        {fehler && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fehler}
          </Alert>
        )}
        {besitz === null && !fehler && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={22} />
          </Box>
        )}

        {besitz && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Portal</TableCell>
                <TableCell>Was dort passiert</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {besitz.map((o) => {
                const b = beschreibung(o);
                const an = !abgewaehlt.has(Number(o.site_id));
                return (
                  <TableRow key={o.site_id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={an}
                        inputProps={{ 'aria-label': `Portal ${o.site_key} einbeziehen` }}
                        onChange={() =>
                          setAbgewaehlt((s) => {
                            const n = new Set(s);
                            if (n.has(Number(o.site_id))) n.delete(Number(o.site_id));
                            else n.add(Number(o.site_id));
                            return n;
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={o.site_key} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={b.ton === 'warning' ? 'warning.main' : 'text.primary'}
                      >
                        {an ? b.text : 'wird NICHT angefasst'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {besitz && gewaehlt.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Kein Portal gewählt — es gäbe nichts zu tun.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onAbbrechen}>Abbrechen</Button>
        <Button
          variant="contained"
          disabled={!besitz || gewaehlt.length === 0}
          onClick={() =>
            // Alle gewählt → kein Filter (der Server nimmt dann von sich aus alle). Sonst genau
            // die gewählten. Das hält den Normalfall frei von einer Liste, die veralten kann.
            onAusspielen(alleGewaehlt ? [] : gewaehlt.map((o) => Number(o.site_id)))
          }
        >
          {/* ⚠ Singular/Plural mitfuehren: „Auf 1 gewaehlte Portale ausspielen" stand im
              Abnahme-Durchlauf auf dem Schirm. Ein Knopf, der falsch flektiert, liest sich wie eine
              Attrappe — und dieser hier soll gerade Vertrauen tragen. */}
          {alleGewaehlt
            ? `Auf alle ${gewaehlt.length} Portale ausspielen`
            : gewaehlt.length === 1
              ? 'Auf 1 gewähltes Portal ausspielen'
              : `Auf ${gewaehlt.length} gewählte Portale ausspielen`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SnippetAusspielenDialog;
